import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { Input } from '@app/components/common/inputs/Input/Input';
import { Modal } from '@app/components/common/Modal/Modal';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationController } from '@app/controllers/notificationController';
import axios from 'axios';
import { IDealRoomCategory } from '@app/interfaces/deal-room-category';
import { createCategory, updateCategory } from '@app/api/deal-room-category';

interface AddCategoryProps {
  open: boolean;
  dealRoomId: string;
  dealRoomCategory?: IDealRoomCategory | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const AddCategory: React.FC<AddCategoryProps> = ({
  open,
  dealRoomId,
  dealRoomCategory,
  onSuccess,
  onCancel,
}) => {
  const [form] = BaseForm.useForm();
  const { t } = useTranslation();

  const [loader, setLoader] = useState(false);

  const onSubmit = async (values: Partial<IDealRoomCategory>) => {
    setLoader(true);
    try {
      const dealRoomCategoryId = dealRoomCategory?.id || null;

      const payload = {
        ...values,
        dealRoom: dealRoomId,
      };

      dealRoomCategoryId ? await updateCategory(dealRoomCategoryId as string, values) : await createCategory(payload);
      notificationController.success({
        message: t(
          dealRoomCategoryId
            ? 'dealRooms.selectMenu.categoryUpdatedSuccessfully'
            : 'dealRooms.selectMenu.categoryCreatedSuccessfully',
        ),
      });
      onSuccess();
    } catch (error) {
      if (!axios.isCancel(error)) notificationController.error({ message: (error as Error).message });
    } finally {
      setLoader(false);
    }
  };

  const onOk = () => {
    form.submit();
  };

  useEffect(() => {
    if (dealRoomCategory) {
      form.setFieldsValue({
        name: dealRoomCategory.name,
      });
    }
  }, [form, dealRoomCategory]);

  return (
    <Modal
      size="large"
      title={t(dealRoomCategory?.id ? 'dealRooms.selectMenu.updateCategory' : 'dealRooms.selectMenu.createCategory')}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      okText={t(dealRoomCategory?.id ? 'dealRooms.selectMenu.update' : 'dealRooms.selectMenu.create')}
      cancelText={t('common.cancel')}
      confirmLoading={loader}
    >
      <BaseForm form={form} layout="vertical" name="dealRoomCategoryForm" onFinish={onSubmit} style={{ width: '100%' }}>
        <BaseForm.Item
          name="name"
          rules={[{ required: true, message: t('dealRooms.selectMenu.categoryNameRequired') }]}
          label={t('dealRooms.selectMenu.categoryName')}
        >
          <Input placeholder={t('dealRooms.selectMenu.categoryName')} />
        </BaseForm.Item>
      </BaseForm>
    </Modal>
  );
};
